// Calendar clients poll the subscription feed on their own schedule (Apple
// every few hours, Google sometimes 24h+), so we treat a fetch within this
// window as "still synced" rather than expecting real-time accuracy.
export const CALENDAR_SYNC_WINDOW_HOURS = 48;
export const CALENDAR_SYNC_RESET_EVENT = "calendar-sync-reset";

/**
 * Parse the timestamps the backend writes.
 *
 * powertools' aws_datetime emits a non-standard offset that includes seconds
 * (e.g. "2026-07-28T15:51:01.787+02:00:00"). `new Date()` rejects that outright,
 * so trim the offset back to ±HH:MM before parsing.
 */
export const parseBackendDate = (value: string): Date | null => {
  const normalized = value.trim().replace(/([+-]\d{2}:\d{2}):\d{2}$/, "$1");
  const parsed = new Date(normalized);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const SWEDISH_MONTHS = [
  "Januari",
  "Februari",
  "Mars",
  "April",
  "Maj",
  "Juni",
  "Juli",
  "Augusti",
  "September",
  "Oktober",
  "November",
  "December",
];

/** "27 April 2026 - Kl. 19:32" */
export const formatLastSynced = (lastSyncedAt?: string | null): string => {
  if (!lastSyncedAt) return "";
  const d = parseBackendDate(lastSyncedAt);
  if (!d) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${d.getDate()} ${
    SWEDISH_MONTHS[d.getMonth()]
  } ${d.getFullYear()} - Kl. ${hh}:${mm}`;
};

export const isCalendarSynced = (lastSyncedAt?: string | null): boolean => {
  if (!lastSyncedAt) return false;
  const parsed = parseBackendDate(lastSyncedAt);
  if (!parsed) return false;
  const hoursSince = (Date.now() - parsed.getTime()) / (1000 * 60 * 60);
  return hoursSince >= 0 && hoursSince <= CALENDAR_SYNC_WINDOW_HOURS;
};

// Host serving the calendar feed. The custom domain only exists on prod (the
// API Gateway custom domain is gated on env == "prod" in terraform), so staging
// must point at its execute-api host via REACT_APP_CALENDAR_FEED_BASE.
const CALENDAR_FEED_HOST = (
  process.env.REACT_APP_CALENDAR_FEED_BASE || "kalender.volley.kltk.se"
)
  .replace(/^https?:\/\//, "")
  .replace(/^webcal:\/\//, "")
  .replace(/\/+$/, "");

// Subscription URL — this is the only thing that marks a member as synced,
// because the calendar client keeps fetching it.
export const getCalendarSubscribeUrl = (token: string): string =>
  `webcal://${CALENDAR_FEED_HOST}/abonnera/${token}`;

const pad = (n: number) => String(n).padStart(2, "0");

// ICS wants UTC basic format: 20260728T133000Z
const toIcsUtc = (date: Date): string =>
  `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(
    date.getUTCDate()
  )}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(
    date.getUTCSeconds()
  )}Z`;

const escapeIcsText = (value: string): string =>
  value.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,");

/**
 * Build a one-off .ics for a single booking and trigger a download.
 *
 * Deliberately generated in the browser: it never touches the calendar feed,
 * so exporting one booking can NOT make the member look "synced".
 */
export const downloadBookingIcs = (
  booking: {
    datetime_start: string;
    booking_id?: string;
    // Callers spread a whole booking object in; allow the extra fields.
    [key: string]: any;
  },
  courtName: string
): void => {
  const start = new Date(booking.datetime_start);
  if (isNaN(start.getTime())) return;
  const end = new Date(start.getTime() + 60 * 60 * 1000);

  const uid = `${booking.booking_id || start.getTime()}@kltk.se`;
  const summary = escapeIcsText(`Tennistid KLTK${courtName ? ` – Bana ${courtName}` : ""}`);

  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//KLTK//Booking//SV",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${toIcsUtc(new Date())}`,
    `DTSTART:${toIcsUtc(start)}`,
    `DTEND:${toIcsUtc(end)}`,
    `SUMMARY:${summary}`,
    "LOCATION:KLTK",
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");

  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "bokning.ics";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
